/**
 * The operator's sign-in: a Google button, and nothing else on the page. F-125.
 *
 * **This is not the learner's sign-in and must never become it.** Until F-125 the console
 * rendered `../SignIn` — the email-and-code form — because a learner session *was* the
 * operator's session. Rendering it here now would be worse than useless: somebody would sign
 * in as a learner, `/api/admin/me` would still answer 401, and the loop would look like a bug
 * in the sign-in rather than the wrong sign-in entirely.
 *
 * English-only, no `t()`. See the note at the top of `Console.tsx`.
 *
 * ---
 *
 * ### The order matters and each step is waiting on the one before it
 *
 * 1. `GET /api/admin/nonce` — the API mints a nonce, hands it back **and** sets it as an
 *    `HttpOnly` cookie. The client id comes back on the same response, so this bundle carries
 *    no configuration: changing the OAuth client is a deploy of the API, not of the site.
 * 2. Google's script is loaded, once, and `initialize` is given the client id and that nonce.
 * 3. Google signs an ID token **with the nonce inside it** and hands it to the callback.
 * 4. `POST /api/admin/google` verifies the signature against Google's keys, checks the nonce
 *    against the cookie, applies the domain rule, and sets our own operator cookie.
 *
 * The nonce is the whole of the replay protection: without it, any ID token minted for our
 * (public) client id anywhere on the web would be presentable here for its full hour.
 *
 * ### Why the script is injected rather than put in `index.html`
 *
 * It would otherwise be fetched by every learner on every page load of a product none of them
 * can use this part of — a third-party request, on the critical path, for a screen exactly one
 * person ever opens. Injected here it is requested only by a browser that has already asked
 * `/api/admin/me` and been told to sign in.
 *
 * ### What a failure looks like, and why it says so little
 *
 * One message: sign-in did not work. The API answers every refusal — wrong domain, no `hd`,
 * unverified email, a replayed token — with the same 404, because a page that named the rule
 * would tell a stranger both that they had reached a real console and what to go and obtain.
 * The reason is in `operator_sign_in` in CloudWatch, which is where it is useful and where only
 * we can read it. The one thing worth distinguishing on screen is *not configured* versus
 * *refused*, because the first is the owner's own deploy problem and not a person being turned
 * away.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { isError } from './api';

/** Google's script, and the one global it defines. */
const GIS_SRC = 'https://accounts.google.com/gsi/client';

/**
 * The slice of Google Identity Services this file uses.
 *
 * Declared locally rather than pulled from `@types/google.one-tap`: four fields, against a
 * package nobody else in this repository has, for a screen with one user. The cast is narrow
 * and everything it touches is checked for existence before use, because the script is loaded
 * from a third party and "it was there in the documentation" is not a guarantee at runtime.
 */
interface GoogleIdentityServices {
  accounts: {
    id: {
      initialize(config: {
        client_id: string;
        nonce: string;
        callback: (response: { credential?: string }) => void;
        auto_select?: boolean;
        cancel_on_tap_outside?: boolean;
        ux_mode?: 'popup' | 'redirect';
      }): void;
      renderButton(
        parent: HTMLElement,
        options: { theme?: string; size?: string; text?: string; width?: number },
      ): void;
    };
  };
}

function gis(): GoogleIdentityServices | undefined {
  return (window as unknown as { google?: GoogleIdentityServices }).google;
}

/**
 * Loads Google's script once per page, and resolves when the global actually exists.
 *
 * Keyed on the `src` already being in the document rather than on a module-level flag, so a
 * remount — which React does in development's strict mode, twice, immediately — does not
 * append a second `<script>` and register a second callback for one press of the button.
 *
 * The `load` event is not enough on its own: it fires when the file arrived, and what this
 * needs is `window.google.accounts.id` to be defined. Hence the check after it.
 */
function loadGoogleScript(): Promise<boolean> {
  return new Promise((resolve) => {
    if (gis()) return resolve(true);

    const existing = document.querySelector<HTMLScriptElement>(`script[src="${GIS_SRC}"]`);
    const script = existing ?? document.createElement('script');
    const done = () => resolve(Boolean(gis()));

    script.addEventListener('load', done, { once: true });
    // A blocked script — an extension, a corporate proxy, an offline laptop — must not leave
    // this screen spinning for ever. It resolves false and the page says so.
    script.addEventListener('error', () => resolve(false), { once: true });

    if (!existing) {
      script.src = GIS_SRC;
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }
  });
}

type Phase = 'loading' | 'ready' | 'submitting' | 'refused' | 'unavailable' | 'not_configured';

export default function OperatorSignIn({ onSignedIn }: { onSignedIn: () => void }) {
  const [phase, setPhase] = useState<Phase>('loading');
  const button = useRef<HTMLDivElement | null>(null);

  /**
   * What Google hands back: a signed JWT, straight to our API.
   *
   * Nothing is decoded here and nothing is stored. The browser is a courier — every claim in
   * that token is attacker-controlled text until the API has checked the signature against
   * Google's keys, and a console that read the email out of it to decide what to draw would be
   * a console making an authorisation decision in a place anybody can edit.
   */
  const submit = useCallback(
    async (credential: string | undefined) => {
      if (!credential) {
        setPhase('refused');
        return;
      }
      setPhase('submitting');
      const response = await fetch('/api/admin/google', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ credential }),
      }).catch(() => undefined);

      if (response?.ok) {
        onSignedIn();
        return;
      }
      setPhase('refused');
    },
    [onSignedIn],
  );

  useEffect(() => {
    let stale = false;

    void (async () => {
      const started = await fetch('/api/admin/nonce', { credentials: 'same-origin' })
        .then((response) => (response.ok ? (response.json() as Promise<unknown>) : undefined))
        .catch(() => undefined);
      if (stale) return;

      const config = started as { nonce?: unknown; clientId?: unknown } | undefined;
      if (typeof config?.nonce !== 'string' || typeof config.clientId !== 'string') {
        // 404 from `/api/admin/nonce` means the client id is not configured. Distinguished
        // from a refusal because it is a deploy fault and not a person being turned away, and
        // because the owner is the only one who will ever see it.
        setPhase('not_configured');
        return;
      }

      const loaded = await loadGoogleScript();
      if (stale) return;
      const google = gis();
      if (!loaded || !google || !button.current) {
        setPhase('unavailable');
        return;
      }

      google.accounts.id.initialize({
        client_id: config.clientId,
        nonce: config.nonce,
        callback: (response) => void submit(response.credential),
        // No One Tap and no silent re-sign-in. The operator's browser is frequently signed in
        // to a personal Google account as well, and being admitted automatically as the wrong
        // one produces a 404 on the console with nothing on screen to explain it.
        auto_select: false,
        cancel_on_tap_outside: true,
        ux_mode: 'popup',
      });
      google.accounts.id.renderButton(button.current, {
        theme: 'outline',
        size: 'large',
        text: 'signin_with',
      });
      setPhase('ready');
    })();

    return () => {
      stale = true;
    };
  }, [submit]);

  return (
    <section className="admin-signin">
      <h1>Sign in</h1>
      <p className="sub">
        A Google Workspace account at a company domain. Nothing else on this site signs you in
        here.
      </p>

      {/* Rendered into by Google's script, and kept mounted in every phase: unmounting it on a
          refusal would destroy the element the script is holding, so a second attempt would
          silently draw no button at all. */}
      <div ref={button} className="admin-signin-button" />

      {phase === 'loading' && <p className="sub">Loading Google sign-in…</p>}
      {phase === 'submitting' && <p className="sub">Checking…</p>}
      {phase === 'refused' && (
        <p className="sub bad">
          That did not sign you in. Try again with your company account — and if it keeps
          refusing, the reason is in <code>operator_sign_in</code> in <code>/nabu/api</code>.
        </p>
      )}
      {phase === 'unavailable' && (
        <p className="sub bad">Google’s sign-in script did not load. Check the network.</p>
      )}
      {phase === 'not_configured' && (
        <p className="sub bad">
          Sign-in is not configured: the API has no Google client id. See{' '}
          <code>GOOGLE_CLIENT_ID</code> in <code>infra/lib/config.ts</code>.
        </p>
      )}
    </section>
  );
}
